import { api } from './api';
import type { AxiosError } from 'axios';
import { createEnhancedError } from '../utils/error-handling';

// Enum types matching backend

export enum GewType {
  GESAMT = 'Wohnung gesamt',
  NEUBAU = 'Wohnung Neubau',
  BESTAND = 'Wohnung Bestand',
}

export enum PropertyQuality {
  MAESSIG = 'mäßig',
  GUT = 'gut',
  SEHR_GUT = 'sehr gut',
}

// Display labels for Quality
export const PropertyQualityLabels: Record<PropertyQuality, string> = {
  [PropertyQuality.MAESSIG]: 'Mäßig',
  [PropertyQuality.GUT]: 'Gut',
  [PropertyQuality.SEHR_GUT]: 'Sehr Gut',
};

export interface PropertyFeatures {
  [key: string]: any;
}

export interface PropertyRatings {
  [key: string]: any;
}

// Property data interface matching backend DTO
export interface PropertyData {
  id: string;
  // Common fields
  address: string;
  streetName?: string;
  houseNumber?: string;
  city?: string;
  country?: string;
  latitude?: number;
  longitude?: number;
  gewType?: GewType;
  quality?: PropertyQuality;
  // ETW fields
  area?: number;

  // Financial data
  kaufdatum?: string;
  kaufpreis?: number;
  currentValue?: number;
  gain: number;
  gainPercentage: number;
  features: PropertyFeatures;
  ratings: PropertyRatings;
  sellAmount?: number;
  sellDate?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface PropertyFile {
  id: string;
  filename: string;
  s3Key: string;
  mimeType: string;
  size: number;
  propertyId: string;
  url: string; // presigned url for viewing/downloading
  createdAt: string;
  updatedAt: string;
}

// Create property DTO
export interface CreatePropertyDto {
  address: string;
  streetName?: string;
  houseNumber: string;
  city?: string;
  country?: string;
  latitude?: number;
  longitude?: number;
  gewType?: GewType;
  quality?: PropertyQuality;
  // ETW fields
  area?: number;
  features?: PropertyFeatures;
  ratings: PropertyRatings;
  // Financial data
  kaufdatum?: string;
  kaufpreis?: number;
  currentValue?: number;
}

// DTO for selling a property
export interface SellPropertyDto {
  sellAmount: number;
  sellDate: string;
}

// DTO for buying a property
export interface BuyPropertyDto {
  kaufpreis: number;
  kaufdatum: string;
}

// Enhanced error handling for property-specific errors
const handlePropertyError = (error: AxiosError): never => {
  throw createEnhancedError(error);
};

// Property API service
export const propertyApi = {
  // Get all properties for the authenticated user
  getAll: async (): Promise<PropertyData[]> => {
    try {
      const response = await api.get<PropertyData[]>('/properties');
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      throw new Error('Failed to fetch properties');
    } catch (error) {
      return handlePropertyError(error as AxiosError);
    }
  },

  // Get a single property by ID
  getById: async (id: string): Promise<PropertyData> => {
    try {
      const response = await api.get<PropertyData>(`/properties/${id}`);
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      throw new Error('Property not found');
    } catch (error) {
      return handlePropertyError(error as AxiosError);
    }
  },

  // Create a new property
  create: async (property: CreatePropertyDto): Promise<PropertyData> => {
    try {
      const response = await api.post<PropertyData>('/properties', property);
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      throw new Error('Failed to create property');
    } catch (error) {
      return handlePropertyError(error as AxiosError);
    }
  },

  // Update/sell an existing property
  // Sell a property
  sell: async (
    id: string,
    property: SellPropertyDto,
  ): Promise<PropertyData> => {
    try {
      const response = await api.put<PropertyData>(
        `/properties/${id}/sell`,
        property,
      );
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      throw new Error('Failed to sell property');
    } catch (error) {
      return handlePropertyError(error as AxiosError);
    }
  },

  // Buy a property
  buy: async (
    id: string,
    property: BuyPropertyDto,
  ): Promise<PropertyData> => {
    try {
      const response = await api.put<PropertyData>(
        `/properties/${id}/buy`,
        property,
      );
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      throw new Error('Failed to buy property');
    } catch (error) {
      return handlePropertyError(error as AxiosError);
    }
  },

  // Delete a property
  delete: async (id: string): Promise<void> => {
    try {
      await api.delete(`/properties/${id}`);
      // DELETE returns 204 No Content, so no data to process
    } catch (error) {
      handlePropertyError(error as AxiosError);
    }
  },

  // Evaluate a property
  evaluate: async (id: string): Promise<QuickEvaluationResponse> => {
    try {
      const response = await api.post<QuickEvaluationResponse>(
        `/properties/${id}/evaluate`,
      );
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      throw new Error('Failed to quick evaluate property');
    } catch (error) {
      return handlePropertyError(error as AxiosError);
    }
  },

  // Get property evaluations
  getEvaluations: async (
    id: string,
    period?: '1month' | '6months' | '12months' | '5years' | 'all',
  ): Promise<PropertyEvaluation[]> => {
    try {
      const params = period ? { period } : {};
      const response = await api.get<PropertyEvaluation[]>(
        `/properties/${id}/evaluations`,
        { params },
      );
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      throw new Error('Failed to fetch property evaluations');
    } catch (error) {
      return handlePropertyError(error as AxiosError);
    }
  },

  // Get property city distribution
  getCityDistribution: async (): Promise<
    { label: string; value: number }[]
  > => {
    try {
      const response = await api.get<{ label: string; value: number }[]>(
        '/properties/stats/city-distribution',
      );
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      throw new Error('Failed to fetch city distribution');
    } catch (error) {
      return handlePropertyError(error as AxiosError);
    }
  },

  // Get condition classification config
  getConditionClassificationConfig: async (gewType?: string): Promise<any> => {
    try {
      const response = await api.get<any>(
        '/properties/condition-classification/config',
        {
          params: gewType ? { gewType } : {},
        },
      );
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      throw new Error('Failed to fetch condition classification config');
    } catch (error) {
      return handlePropertyError(error as AxiosError);
    }
  },

  // Get property files
  getFiles: async (propertyId: string): Promise<PropertyFile[]> => {
    try {
      const response = await api.get<PropertyFile[]>(
        `/properties/${propertyId}/files`,
      );
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      return [];
    } catch (error) {
      return handlePropertyError(error as AxiosError);
    }
  },

  // Upload property file
  uploadFile: async (propertyId: string, file: File): Promise<PropertyFile> => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      // We use apiClient directly here to override headers easily
      const response = await import('./api').then((m) =>
        m.default.post(`/properties/${propertyId}/files`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }),
      );
      if (response.data.success && response.data.data) {
        return response.data.data as PropertyFile;
      }
      throw new Error('Failed to upload file');
    } catch (error) {
      return handlePropertyError(error as AxiosError);
    }
  },

  // Upload local market data XLSX file (Admin only)
  uploadLocalMarketData: async (file: File): Promise<{ success: boolean; message: string; data: any }> => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await import('./api').then((m) =>
        m.default.post(`/properties/local-market-data/import`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }),
      );
      if (response.data.success) {
        return response.data;
      }
      throw new Error(response.data.message || 'Failed to upload local market data');
    } catch (error) {
      return handlePropertyError(error as AxiosError);
    }
  },

  // Delete property file
  deleteFile: async (propertyId: string, fileId: string): Promise<void> => {
    try {
      await api.delete(`/properties/${propertyId}/files/${fileId}`);
    } catch (error) {
      handlePropertyError(error as AxiosError);
    }
  },
};

// Quick evaluation response interface (from ds-s API)
export interface QuickEvaluationResponse {
  error_on?: number; // 0-1
  exists_reason?: number; // 0-1, Gibt es Fehler?
  exists_warning?: number; // 0-1, Gibt es Warnungen?
  firma?: string;
  gstpreis?: number; // Grundstückspreis gesamt [gst]
  gstpreis_lwr?: number; // Grundstückspreis Untergrenze [gst]
  gstpreis_upr?: number; // Grundstückspreis Obergrenze [gst]
  id?: string; // UUID, ImmAzing BewertungsID
  lat?: number;
  lon?: number;
  objektnr_intern?: number;
  openimmo_anid?: string;
  reason_plaus?: string; // JSON string
  reason_req?: string; // JSON string
  version?: string; // Versionsnummer, example: "v20"
  vw?: number; // Vergleichswert [etw/efh]
  vw_lwr?: number; // Vergleichswert Untergrenze [etw/efh]
  vw_upr?: number; // Vergleichswert Obergrenze [etw/efh]
  warn_num?: string; // JSON string
  warn_plaus?: string; // JSON string
  warn_str?: string; // JSON string
  immazing_link?: string; // JSON string
}

// Property evaluation interface
export interface PropertyEvaluation {
  id: string;
  vw?: number;
  vwLwr?: number; // camelCase to match backend entity
  vwUpr?: number; // camelCase to match backend entity
  timestamp: string;
  createdAt: string;
  updatedAt: string;
}

// Places API service
export const placesApi = {
  getAutocomplete: async (input: string): Promise<any[]> => {
    if (!input) return [];
    try {
      const response = await api.get('/places/autocomplete', {
        params: { input },
      });
      if (response.data.success && response.data.data) {
        return response.data.data as any[];
      }
      return [];
    } catch (error) {
      console.error('Error fetching autocomplete suggestions:', error);
      return [];
    }
  },

  getPlaceDetails: async (placeId: string): Promise<any> => {
    try {
      const response = await api.get('/places/place-details', {
        params: { placeId },
      });
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      return null;
    } catch (error) {
      console.error('Error fetching place details:', error);
      return null;
    }
  },

  getCities: async (input: string): Promise<string[]> => {
    if (!input) return [];
    try {
      const response = await api.get('/places/cities', { params: { input } });
      if (response.data.success && response.data.data) {
        return response.data.data as string[];
      }
      return [];
    } catch (error) {
      console.error('Error fetching city suggestions:', error);
      return [];
    }
  },

  getCityByCoordinates: async (
    lat: number,
    lng: number,
  ): Promise<string | null> => {
    try {
      const response = await api.get<{ city: string | null }>(
        '/places/city-by-coordinates',
        {
          params: { lat, lng },
        },
      );
      if (response.data.success && response.data.data) {
        return response.data.data.city;
      }
      return null;
    } catch (error) {
      console.error('Error fetching city by coordinates:', error);
      return null;
    }
  },
};
